(() => {
  'use strict';

  if (window.__FRK_PDP_SOFT_NAVIGATION__) return;
  window.__FRK_PDP_SOFT_NAVIGATION__ = true;

  const PRODUCT_LINK_SELECTOR = [
    '[data-frk-roof-size-selector] [data-frk-size-chip]',
    '[data-frk-roof-size-selector] [data-frk-size-sheet-option]',
    '[data-frk-roof-size-selector] [data-frk-strength-chip]',
    '[data-frk-config-strength-link]',
    '[data-frk-pack-size-wrapper] [data-frk-pack-card]'
  ].join(',');

  const SELECTOR = '[data-frk-roof-size-selector]';
  const PACK_WRAPPER = '[data-frk-pack-size-wrapper]';
  const PACK_SELECT = '[data-frk-pack-size-selector]';

  // Scroll anchor kept steady across a swap: the kit selector on kit pages,
  // otherwise the pack-size block on single-product pages.
  function anchorIn(scope) {
    const root = scope || document;
    if (root.matches?.(SELECTOR)) return root;
    return root.querySelector?.(SELECTOR) || root.querySelector?.(PACK_WRAPPER) || null;
  }
  const STORAGE_KEY = 'frk-configurator-open-step';
  const HARD_NAV_RESTORE_KEY = 'frk-pdp-hard-nav-restore-v1';
  const LOADING_CLASS = 'frk-pdp-soft-nav-loading';
  const TIMEOUT_MS = 9000;
  const DESKTOP_QUERY = '(min-width: 768px) and (hover: hover) and (pointer: fine)';
  const PREFETCH_DELAY_MS = 90;
  const PREFETCH_MAX_AGE_MS = 60000;
  const PREFETCH_CACHE_LIMIT = 4;

  let requestController = null;
  let requestNumber = 0;
  let fallbackTimer = null;
  let isPopNavigation = false;
  let pendingPrefetch = null;
  const documentCache = new Map();

  function isDesktopOptimised() {
    return Boolean(window.matchMedia?.(DESKTOP_QUERY).matches);
  }

  function isPlainPrimaryClick(event) {
    return event.button === 0
      && !event.metaKey
      && !event.ctrlKey
      && !event.shiftKey
      && !event.altKey;
  }

  function isSameOriginProductUrl(url) {
    return url.origin === window.location.origin && /^\/products\//.test(url.pathname);
  }

  function cacheKey(url) {
    return `${url.origin}${url.pathname}${url.search}`;
  }

  function pruneDocumentCache() {
    const now = Date.now();
    documentCache.forEach((entry, key) => {
      if (now - entry.createdAt > PREFETCH_MAX_AGE_MS) documentCache.delete(key);
    });

    while (documentCache.size > PREFETCH_CACHE_LIMIT) {
      documentCache.delete(documentCache.keys().next().value);
    }
  }

  function abortError() {
    try {
      return new DOMException('Navigation aborted', 'AbortError');
    } catch (_) {
      const error = new Error('Navigation aborted');
      error.name = 'AbortError';
      return error;
    }
  }

  function waitForPromise(promise, signal) {
    if (!signal) return promise;
    if (signal.aborted) return Promise.reject(abortError());

    return new Promise((resolve, reject) => {
      const onAbort = () => reject(abortError());
      signal.addEventListener('abort', onAbort, { once: true });
      promise.then(
        (value) => {
          signal.removeEventListener('abort', onAbort);
          resolve(value);
        },
        (error) => {
          signal.removeEventListener('abort', onAbort);
          reject(error);
        }
      );
    });
  }

  function requestedStep(link) {
    if (link.matches('[data-frk-strength-chip], [data-frk-config-strength-link]')) return 'strength';
    return 'size';
  }

  function currentOpenStep() {
    const open = document.querySelector(`${SELECTOR} [data-frk-config-step].is-expanded`);
    return open?.dataset.frkConfigStep || 'size';
  }

  function storeNextOpenStep(step) {
    try {
      sessionStorage.setItem(STORAGE_KEY, step);
    } catch (_) {
      // Normal links still work if session storage is unavailable.
    }
  }

  function clearTransientUi() {
    document.documentElement.classList.remove(LOADING_CLASS);
    document.body.classList.remove(
      'frk-size-sheet-open',
      'frk-config-help-open',
      'frk-substrate-sheet-open',
      'frk-osb-sheet-open'
    );
  }

  function closeOpenDialogs() {
    document.querySelectorAll('dialog[open]').forEach((dialog) => {
      try {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      } catch (_) {
        dialog.removeAttribute('open');
      }
    });
  }

  function removeDetachedConfiguratorDrawers() {
    // The strength/coverage help drawer is intentionally moved to <body> by
    // frk-roof-size-selector.js. Remove the old detached instance before the
    // replacement selector moves its new drawer into <body>.
    document.querySelectorAll('body > [data-frk-config-help]').forEach((drawer) => drawer.remove());
  }

  function syncMeta(targetDocument, targetUrl) {
    const targetTitle = targetDocument.querySelector('head title');
    if (targetTitle) document.title = targetTitle.textContent || document.title;

    const copyAttribute = (selector, attribute = 'content') => {
      const target = targetDocument.head?.querySelector(selector);
      const current = document.head?.querySelector(selector);
      if (!target || !current) return;
      const value = target.getAttribute(attribute);
      if (value !== null) current.setAttribute(attribute, value);
    };

    copyAttribute('meta[name="description"]');
    copyAttribute('meta[property="og:title"]');
    copyAttribute('meta[property="og:description"]');
    copyAttribute('meta[property="og:url"]');
    copyAttribute('meta[name="twitter:title"]');
    copyAttribute('meta[name="twitter:description"]');

    const targetCanonical = targetDocument.head?.querySelector('link[rel="canonical"]');
    const currentCanonical = document.head?.querySelector('link[rel="canonical"]');
    if (currentCanonical) {
      const canonicalValue = targetCanonical?.getAttribute('href');
      currentCanonical.href = canonicalValue ? new URL(canonicalValue, targetUrl.href).href : targetUrl.href;
    }
  }

  function setBusy(link, step) {
    document.documentElement.classList.add(LOADING_CLASS);
    document.querySelectorAll(`${SELECTOR} .is-navigating`).forEach((node) => node.classList.remove('is-navigating'));
    link?.classList.add('is-navigating');

    const selector = document.querySelector(SELECTOR);
    if (!selector) return;

    if (step === 'size') {
      const status = selector.querySelector('[data-frk-size-status]');
      const value = link?.dataset.frkSize;
      if (status && value) status.textContent = `Loading ${value}m² kit…`;
    } else {
      const status = selector.querySelector('[data-frk-strength-status]');
      const value = link?.dataset.frkStrength
        || link?.querySelector('.frk-config-help__strength-title-row strong')?.textContent?.trim();
      if (status && value) status.textContent = `Loading ${value} kit…`;
    }
  }

  function rememberCurrentHistoryEntry() {
    const existing = history.state && typeof history.state === 'object' ? history.state : {};
    history.replaceState({
      ...existing,
      frkPdpSoft: true,
      frkUrl: window.location.href,
      frkOpenStep: currentOpenStep(),
      frkScrollY: window.scrollY
    }, '', window.location.href);
  }

  function pushTargetHistory(targetUrl, step) {
    history.pushState({
      frkPdpSoft: true,
      frkUrl: targetUrl.href,
      frkOpenStep: step,
      frkScrollY: window.scrollY
    }, '', `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`);
  }

  function updateCurrentHistoryScroll(step) {
    const existing = history.state && typeof history.state === 'object' ? history.state : {};
    if (!existing.frkPdpSoft) return;
    history.replaceState({
      ...existing,
      frkUrl: window.location.href,
      frkOpenStep: step || existing.frkOpenStep || currentOpenStep(),
      frkScrollY: window.scrollY
    }, '', window.location.href);
  }

  function dispatchReinitialise(newMain, step) {
    // All FRK PDP components already support Shopify's section-load lifecycle.
    // Dispatching from the new <main> gives those listeners the new subtree as scope.
    newMain.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('frk:pdp-soft-navigated', {
      detail: { scope: newMain, step }
    }));

    // The selector binds its accordion before its collection discovery resolves,
    // so opening the requested step does not wait for any product-index requests.
    window.FRKRoofKitSelector?.openStep?.(step, newMain);

    remountJudgemeWidgets(newMain);
  }

  // Judge.me mounts its review widgets once, on first page load, so the swapped-in <main>
  // holds unmounted placeholders (raw histogram text and group notice). Re-running each
  // widget's own entry script, from the folder Judge.me's loader came from, mounts them.
  // ponytail: depends on Judge.me's data-entry-point markup; swap for an official reload API if one appears.
  function remountJudgemeWidgets(scope) {
    const loader = document.querySelector('script[src*="judgeme"][src*="/loader.js"]');
    if (!loader) return;
    const base = loader.src.split('loader.js')[0];
    const entries = new Set([...scope.querySelectorAll('.jdgm-widget[data-entry-point]')]
      .filter((widget) => !/done-setup/.test(widget.className))
      .map((widget) => widget.dataset.entryPoint));
    entries.forEach((entry) => {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = `${base}${entry}?frk-remount=${Date.now()}`;
      document.head.appendChild(script);
    });
  }

  async function fetchTargetDocument(targetUrl, signal) {
    const response = await fetch(targetUrl.href, {
      credentials: 'same-origin',
      signal,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-FRK-PDP-Soft-Navigation': '1'
      }
    });

    if (!response.ok) throw new Error(`PDP request failed (${response.status})`);
    const text = await response.text();
    return new DOMParser().parseFromString(text, 'text/html');
  }

  function warmTargetDocument(targetUrl) {
    if (!isDesktopOptimised() || !isSameOriginProductUrl(targetUrl)) return null;
    if (targetUrl.pathname === window.location.pathname && targetUrl.search === window.location.search) return null;

    pruneDocumentCache();
    const key = cacheKey(targetUrl);
    const existing = documentCache.get(key);
    if (existing) return existing.promise;

    const promise = fetchTargetDocument(targetUrl)
      .then((targetDocument) => {
        const targetMain = targetDocument.querySelector('main');
        const targetProductInfo = targetMain?.querySelector('product-info[data-section-type="product-page"]');
        if (!targetMain || !targetProductInfo) {
          throw new Error('Prefetched product page did not contain the expected PDP markup');
        }
        return targetDocument;
      });

    documentCache.set(key, { promise, createdAt: Date.now() });
    pruneDocumentCache();
    promise.catch(() => {
      if (documentCache.get(key)?.promise === promise) documentCache.delete(key);
    });
    return promise;
  }

  function scheduleTargetPrefetch(targetUrl) {
    if (!isDesktopOptimised() || !isSameOriginProductUrl(targetUrl)) return false;

    const key = cacheKey(targetUrl);
    pruneDocumentCache();
    if (documentCache.has(key)) return true;
    if (pendingPrefetch?.key === key) return true;

    if (pendingPrefetch?.timer) window.clearTimeout(pendingPrefetch.timer);
    const timer = window.setTimeout(() => {
      if (pendingPrefetch?.key === key) pendingPrefetch = null;
      warmTargetDocument(targetUrl);
    }, PREFETCH_DELAY_MS);
    pendingPrefetch = { key, targetUrl, timer };
    return true;
  }

  function takeTargetDocument(targetUrl, signal) {
    if (!isDesktopOptimised()) return fetchTargetDocument(targetUrl, signal);

    const key = cacheKey(targetUrl);
    pruneDocumentCache();
    let promise = documentCache.get(key)?.promise;

    if (!promise && pendingPrefetch?.key === key) {
      window.clearTimeout(pendingPrefetch.timer);
      pendingPrefetch = null;
      promise = warmTargetDocument(targetUrl);
    }

    return promise ? waitForPromise(promise, signal) : fetchTargetDocument(targetUrl, signal);
  }

  function storeHardNavigationRestore(targetUrl, step) {
    if (!isDesktopOptimised() || !isSameOriginProductUrl(targetUrl)) return;
    const selector = anchorIn(document);
    const selectorTop = selector?.getBoundingClientRect().top;
    if (!Number.isFinite(selectorTop)) return;

    try {
      sessionStorage.setItem(HARD_NAV_RESTORE_KEY, JSON.stringify({
        target: `${targetUrl.pathname}${targetUrl.search}`,
        step,
        selectorTop,
        createdAt: Date.now()
      }));
    } catch (_) {
      // A normal navigation remains available when storage is blocked.
    }
  }

  function clearHardNavigationRestore() {
    try {
      sessionStorage.removeItem(HARD_NAV_RESTORE_KEY);
    } catch (_) {
      // Storage may be blocked without affecting navigation.
    }
  }

  function alignSelectorToViewport(scope, selectorTop) {
    if (!Number.isFinite(selectorTop)) return;
    const selector = anchorIn(scope);
    if (!selector) return;

    const shift = selector.getBoundingClientRect().top - selectorTop;
    if (Math.abs(shift) > 1) window.scrollBy({ top: shift, left: 0, behavior: 'auto' });
  }

  function restoreHardNavigationPosition() {
    if (!isDesktopOptimised()) return;

    let restore = null;
    try {
      restore = JSON.parse(sessionStorage.getItem(HARD_NAV_RESTORE_KEY) || 'null');
    } catch (_) {
      clearHardNavigationRestore();
      return;
    }

    const currentTarget = `${window.location.pathname}${window.location.search}`;
    if (!restore
      || restore.target !== currentTarget
      || !Number.isFinite(restore.selectorTop)
      || Date.now() - Number(restore.createdAt || 0) > 30000) {
      clearHardNavigationRestore();
      return;
    }

    clearHardNavigationRestore();
    const apply = () => {
      alignSelectorToViewport(document, restore.selectorTop);
      window.FRKRoofKitSelector?.openStep?.(restore.step || 'size');
    };
    window.requestAnimationFrame(() => window.requestAnimationFrame(apply));
  }

  function fallbackToNormalNavigation(targetUrl, step) {
    storeNextOpenStep(step);
    storeHardNavigationRestore(targetUrl, step);
    clearTransientUi();
    window.location.assign(targetUrl.href);
  }

  async function navigate(targetUrl, options = {}) {
    const step = options.step || 'size';
    const historyMode = options.historyMode || 'push';
    const restoreScrollY = Number.isFinite(options.restoreScrollY) ? options.restoreScrollY : null;
    const sourceLink = options.sourceLink || null;

    if (!isSameOriginProductUrl(targetUrl)) {
      fallbackToNormalNavigation(targetUrl, step);
      return;
    }

    if (typeof HTMLUpdateUtility === 'undefined' || !document.querySelector('main')) {
      fallbackToNormalNavigation(targetUrl, step);
      return;
    }

    requestNumber += 1;
    const thisRequest = requestNumber;
    requestController?.abort();
    window.clearTimeout(fallbackTimer);
    requestController = new AbortController();

    setBusy(sourceLink, step);
    storeNextOpenStep(step); // also acts as the full-navigation fallback state

    let timedOut = false;
    fallbackTimer = window.setTimeout(() => {
      timedOut = true;
      requestController?.abort();
    }, TIMEOUT_MS);

    const oldMain = document.querySelector('main');
    const oldSelector = anchorIn(document);
    const oldSelectorTop = oldSelector?.getBoundingClientRect().top;

    try {
      const targetDocument = await takeTargetDocument(targetUrl, requestController.signal);
      if (thisRequest !== requestNumber) return;

      const targetMain = targetDocument.querySelector('main');
      const targetProductInfo = targetMain?.querySelector('product-info[data-section-type="product-page"]');
      if (!targetMain || !targetProductInfo) throw new Error('Target product page did not contain the expected PDP markup');

      if (historyMode === 'push') {
        rememberCurrentHistoryEntry();
        pushTargetHistory(targetUrl, step);
      }

      syncMeta(targetDocument, targetUrl);
      closeOpenDialogs();
      removeDetachedConfiguratorDrawers();
      clearTransientUi();

      HTMLUpdateUtility.viewTransition(oldMain, targetMain);

      // HTMLUpdateUtility inserts the replacement immediately before the old
      // (temporarily hidden) main, so querySelector now resolves the new main.
      const newMain = document.querySelector('main');
      if (!newMain || newMain === oldMain) throw new Error('PDP replacement did not complete');

      if (isDesktopOptimised() && restoreScrollY === null) {
        alignSelectorToViewport(newMain, oldSelectorTop);
      }

      dispatchReinitialise(newMain, step);

      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      if (restoreScrollY !== null) {
        window.scrollTo({ top: restoreScrollY, left: 0, behavior: 'auto' });
      } else if (Number.isFinite(oldSelectorTop)) {
        alignSelectorToViewport(newMain, oldSelectorTop);
      }

      clearHardNavigationRestore();
      updateCurrentHistoryScroll(step);
      document.dispatchEvent(new CustomEvent('frk:pdp-soft-navigation-complete', {
        detail: { url: targetUrl.href, step }
      }));
    } catch (error) {
      if (thisRequest !== requestNumber) return;
      if (error?.name === 'AbortError' && !timedOut) return;

      console.warn('[FRK PDP soft navigation] Falling back to normal navigation.', error);
      fallbackToNormalNavigation(targetUrl, step);
    } finally {
      if (thisRequest === requestNumber) {
        window.clearTimeout(fallbackTimer);
        fallbackTimer = null;
        clearTransientUi();
      }
    }
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.(PRODUCT_LINK_SELECTOR);
    if (!link || !isPlainPrimaryClick(event)) return;
    // Sold-out pack cards stay inert (this capture listener runs before the pack selector's own guard).
    if (link.dataset.frkPackAvailable === 'false') {
      event.preventDefault();
      return;
    }
    if (link.target && link.target !== '_self') return;
    if (link.hasAttribute('download')) return;

    const targetUrl = new URL(link.href, window.location.href);
    if (!isSameOriginProductUrl(targetUrl)) return;

    // Let the native link handle "open in new tab" and accessibility fallback.
    // Only a normal primary click is progressively enhanced.
    if (targetUrl.pathname === window.location.pathname && targetUrl.search === window.location.search) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const step = requestedStep(link);
    navigate(targetUrl, { step, historyMode: 'push', sourceLink: link });
  }, true);

  // Pack-size dropdown (5+ sizes): soft-navigate instead of location.assign.
  // Capture phase so frk-pack-size-selector.js's own change handler never runs.
  document.addEventListener('change', (event) => {
    const select = event.target.closest?.(PACK_SELECT);
    if (!select || !select.value) return;
    if (select.options[select.selectedIndex]?.dataset.frkPackAvailable === 'false') return;

    const targetUrl = new URL(select.value, window.location.href);
    if (!isSameOriginProductUrl(targetUrl)) return;
    event.stopPropagation();
    if (targetUrl.pathname === window.location.pathname && targetUrl.search === window.location.search) return;
    navigate(targetUrl, { step: 'size', historyMode: 'push' });
  }, true);

  // Warm the target page while the pointer is over a pack-size card (desktop only).
  document.addEventListener('mouseover', (event) => {
    const link = event.target.closest?.('[data-frk-pack-size-wrapper] [data-frk-pack-card]');
    if (!link || !link.href) return;
    try { scheduleTargetPrefetch(new URL(link.href, window.location.href)); } catch (_) { /* hover prefetch is optional */ }
  }, { passive: true });

  window.addEventListener('popstate', (event) => {
    const state = event.state;
    if (!state?.frkPdpSoft || isPopNavigation) return;

    const targetUrl = new URL(window.location.href);
    if (!isSameOriginProductUrl(targetUrl)) return;

    isPopNavigation = true;
    navigate(targetUrl, {
      step: state.frkOpenStep || 'size',
      historyMode: 'none',
      restoreScrollY: Number.isFinite(state.frkScrollY) ? state.frkScrollY : null
    }).finally(() => { isPopNavigation = false; });
  });

  // Preserve the user's current scroll/open-step state before another soft link
  // is followed. Browser history remains meaningful and normal PDP URLs remain real.
  window.addEventListener('pagehide', () => updateCurrentHistoryScroll(currentOpenStep()));

  window.FRKPDPSoftNavigation = Object.assign(window.FRKPDPSoftNavigation || {}, {
    prefetch(url) {
      if (!isDesktopOptimised()) return false;
      try {
        return scheduleTargetPrefetch(new URL(url, window.location.href));
      } catch (_) {
        return false;
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreHardNavigationPosition, { once: true });
  } else {
    restoreHardNavigationPosition();
  }
})();
